import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import ThemeToggle from './ThemeToggle';
import { navItems, profile } from '../data/profile';

const sectionIds = navItems.map((item) => item.href.slice(1));

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 24);

      // Whichever tracked section sits nearest the top of the viewport wins.
      const probe = window.scrollY + 140;
      let current = '';
      let smallestDistance = Infinity;

      sectionIds.forEach((id) => {
        const element = document.getElementById(id);
        if (!element) return;
        const distance = Math.abs(probe - element.offsetTop);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          current = id;
        }
      });

      setActiveSection(window.scrollY < 200 ? '' : current);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll while the mobile sheet is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  const handleNavClick = useCallback((event, href) => {
    event.preventDefault();
    setIsOpen(false);

    if (href === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const target = document.querySelector(href);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.pageYOffset - 88;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-[100] transition-all duration-500 ease-smooth ${
        scrolled
          ? 'border-b border-slate-200/80 bg-white/80 py-3 backdrop-blur-xl dark:border-white/[0.06] dark:bg-slate-900/75'
          : 'border-b border-transparent bg-transparent py-5'
      }`}
    >
      {/* Reading progress */}
      <motion.div
        aria-hidden="true"
        style={{ scaleX: progress }}
        className="absolute inset-x-0 bottom-0 h-px origin-left bg-gradient-to-r from-blue-500 to-indigo-400"
      />

      <div className="mx-auto max-w-7xl px-6">
        <div className="flex items-center justify-between gap-4">
          {/* Wordmark */}
          <a
            href="/"
            onClick={(event) => handleNavClick(event, '/')}
            className="group flex flex-col rounded-lg text-left"
          >
            <span className="text-lg font-bold tracking-tight text-slate-900 transition-colors group-hover:text-blue-600 md:text-xl dark:text-white dark:group-hover:text-blue-400">
              {profile.name}
              <span className="text-blue-500">.</span>
            </span>
            <span className="text-[10px] font-medium tracking-wide text-slate-500 md:text-[11px] dark:text-slate-400">
              {profile.tagline}
            </span>
          </a>

          {/* Desktop nav */}
          <div className="hidden items-center gap-3 lg:flex">
            <nav aria-label="Primary">
              <ul className="flex items-center gap-0.5 rounded-full border border-slate-200/80 bg-white/60 p-1 backdrop-blur-md dark:border-white/[0.07] dark:bg-white/[0.03]">
                {navItems.map((item) => {
                  const isActive = activeSection === item.href.slice(1);
                  return (
                    <li key={item.href} className="relative">
                      <a
                        href={item.href}
                        onClick={(event) => handleNavClick(event, item.href)}
                        aria-current={isActive ? 'true' : undefined}
                        className={`relative block rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors duration-300 ${
                          isActive
                            ? 'text-slate-900 dark:text-white'
                            : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                        }`}
                      >
                        {isActive && (
                          <motion.span
                            layoutId="nav-pill"
                            aria-hidden="true"
                            className="absolute inset-0 rounded-full bg-slate-900/[0.06] dark:bg-white/[0.09]"
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          />
                        )}
                        <span className="relative">{item.name}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <ThemeToggle />

            <a
              href="#contact"
              onClick={(event) => handleNavClick(event, '#contact')}
              className="rounded-full bg-slate-900 px-5 py-2 text-[13px] font-semibold text-white
                         transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg
                         hover:shadow-blue-500/25 dark:bg-white dark:text-slate-900"
            >
              Get in touch
            </a>
          </div>

          {/* Mobile actions */}
          <div className="flex items-center gap-2 lg:hidden">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setIsOpen((open) => !open)}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200
                         text-slate-800 transition-colors hover:bg-slate-100 dark:border-white/10
                         dark:text-white dark:hover:bg-white/[0.06]"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d={isOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 7h16M4 12h16M4 17h16'}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sheet */}
      <AnimatePresence>
        {isOpen && (
          <motion.nav
            id="mobile-menu"
            aria-label="Mobile"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 top-full border-b border-slate-200 bg-white/95 px-6 py-6
                       backdrop-blur-xl lg:hidden dark:border-white/[0.08] dark:bg-slate-900/95"
          >
            <ul className="flex flex-col">
              {navItems.map((item, index) => (
                <motion.li
                  key={item.href}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <a
                    href={item.href}
                    onClick={(event) => handleNavClick(event, item.href)}
                    className="block border-b border-slate-100 py-3.5 text-[15px] font-medium text-slate-700
                               transition-colors hover:text-blue-600 dark:border-white/[0.06]
                               dark:text-slate-300 dark:hover:text-blue-400"
                  >
                    {item.name}
                  </a>
                </motion.li>
              ))}
            </ul>

            <a
              href="#contact"
              onClick={(event) => handleNavClick(event, '#contact')}
              className="mt-6 block rounded-full bg-slate-900 py-3.5 text-center text-sm font-semibold
                         text-white dark:bg-white dark:text-slate-900"
            >
              Get in touch
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
